import type { Permission } from '@fieldops/shared'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { createContext, useContext, useMemo, type ReactNode } from 'react'
import { useTRPC } from './trpc.js'

export interface CurrentUser {
  id: string
  email: string
  name: string
  /** Their colour on the map's pins and route lines. Nothing else draws it (decision 105). */
  colour: string
  roleId: string
  roleName: string
  branchId: string | null
  permissions: Permission[]
}

interface SessionValue {
  user: CurrentUser | null
  isLoading: boolean
  can: (...required: Permission[]) => boolean
  canAny: (...required: Permission[]) => boolean
  refresh: () => Promise<void>
}

const SessionContext = createContext<SessionValue>({
  user: null,
  isLoading: true,
  can: () => false,
  canAny: () => false,
  refresh: async () => undefined,
})

export const useSession = (): SessionValue => useContext(SessionContext)

/** Throws if used outside an authenticated route. Keeps `user!` out of screens. */
export function useCurrentUser(): CurrentUser {
  const { user } = useSession()
  if (!user) throw new Error('useCurrentUser called outside an authenticated route')
  return user
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const trpc = useTRPC()
  const queryClient = useQueryClient()

  const { data, isLoading } = useQuery({
    ...trpc.auth.me.queryOptions(),
    // The permission set drives what the whole shell renders, so it is worth
    // an occasional refetch even though realtime covers most changes.
    staleTime: 60_000,
    retry: false,
  })

  const value = useMemo<SessionValue>(() => {
    const granted = new Set<Permission>(data?.permissions ?? [])

    return {
      user: (data as CurrentUser | null) ?? null,
      isLoading,
      // The client hides what someone cannot do; the server refuses it. This
      // is presentation, not enforcement — every one of these has a matching
      // check on the procedure.
      can: (...required) => required.every((permission) => granted.has(permission)),
      canAny: (...required) => required.some((permission) => granted.has(permission)),
      refresh: async () => {
        await queryClient.invalidateQueries()
      },
    }
  }, [data, isLoading, queryClient])

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
}
